/**
 * CSSC DataLayer Module
 *
 * Handles Campaign Source Source Code (CSSC) tracking with origin prioritization
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// Import from modular files
import { getPriorityIndex, RESERVED_ORIGINS } from './origins';
import { getReferrerCampaign } from './referrers';
import { validateCampaignCode, setCampaignCookie, getCampaignCookie, getQueryParam } from './utils';
// Re-export from modules for backward compatibility
export { getPriorityIndex, RESERVED_ORIGINS } from './origins';
export { getReferrerCampaign } from './referrers';
export { validateCampaignCode, setCampaignCookie, getCampaignCookie, getQueryParam } from './utils';
/**
 * Creates a new CSSC manager object that handles campaign source tracking with priority-based attribution.
 * The manager validates assertions, manages cookies, and pushes data to the dataLayer for analytics.
 *
 * @returns A CSSC manager object with push functionality and current state tracking
 */
export var createCSSCManager = function () {
    var assertions = [];
    // Track the current known cookie value to avoid unnecessary updates
    var currentKnownCookieValue = getCampaignCookie();
    return {
        assertions: assertions,
        currentValue: null,
        currentOrigin: null,
        push: function (assertion) {
            var origin = assertion.origin, value = assertion.value;
            // Add to assertions array for visibility (regardless of validation)
            assertions.push({ origin: origin, value: value });
            // Also update the dataLayer with the full assertions array for visibility
            if (window.dataLayer && Array.isArray(window.dataLayer)) {
                window.dataLayer.push({
                    cssc_assertions: __spreadArray([], assertions, true)
                });
            }
            // Prevent manual assertions of reserved origins after system gathering is complete
            if (this.initialGatherComplete && RESERVED_ORIGINS.includes(origin)) {
                console.warn("CSSC: Manual assertion of reserved origin '".concat(origin, "' blocked. Reserved origins are only set by gatherCSSCSources()."));
                return;
            }
            // Skip if no value provided
            if (!value) {
                console.warn('CSSC: Ignoring assertion with empty value from origin:', origin);
                return;
            }
            // Validate the campaign code
            var validatedValue = validateCampaignCode(value);
            if (!validatedValue) {
                console.warn('CSSC: Ignoring assertion with invalid campaign code from origin:', origin, 'value:', value);
                return;
            }
            // Check if this origin has higher priority than current
            var newPriority = getPriorityIndex(origin);
            // Ignore origins not in priority list
            if (newPriority === -1)
                return;
            var currentPriority = this.currentOrigin ? getPriorityIndex(this.currentOrigin) : null;
            if (currentPriority === null || newPriority <= currentPriority) {
                // New origin has higher or equal priority
                setCampaignCookie(validatedValue, currentKnownCookieValue);
                currentKnownCookieValue = validatedValue;
                this.currentValue = validatedValue;
                this.currentOrigin = origin;
            }
        }
    };
};
/**
 * Gathers campaign source codes from various built-in sources and asserts them to the CSSC manager.
 * This function checks URL parameters, cookies, HTTP referrers, and sets the global default.
 * After completion, it marks the gathering as complete to prevent manual assertions of reserved origins.
 *
 * @param cssc - The CSSC manager object to assert the gathered sources to
 */
export var gatherCSSCSources = function (cssc) {
    // 1. Check URL query parameter (highest priority)
    var campaignParam = getQueryParam('campaign');
    if (campaignParam) {
        cssc.push({
            origin: 'campaign_query_param',
            value: campaignParam
        });
    }
    // 2. Check for campaign cookie
    var cookieCampaign = getCampaignCookie();
    if (cookieCampaign) {
        cssc.push({
            origin: 'campaign_cookie',
            value: cookieCampaign
        });
    }
    // 3. Check HTTP referrer
    var referrerCampaign = getReferrerCampaign();
    if (referrerCampaign) {
        cssc.push({
            origin: 'http_referrer',
            value: referrerCampaign
        });
    }
    // 4. Assert global default (lowest priority)
    cssc.push({
        origin: 'global_default',
        value: '11840913' // Site-wide default
    });
    // Mark system gathering as complete to prevent manual assertions of reserved origins
    cssc.initialGatherComplete = true;
};
/**
 * Initializes the CSSC module by creating a manager, gathering sources, and processing any existing
 * enqueued assertions from the window.wvusCSSC array. This function is called just once when the
 * CSSC bundle loads. It replaces any existing wvusCSSC array with the full manager implementation
 * (but not before capturing its contents to process as a queue).
 */
export var initializeCSCC = function () {
    // Save existing array if present (to process as a queue)
    var existingQueue = Array.isArray(window.wvusCSSC) ? __spreadArray([], window.wvusCSSC, true) : [];
    // Create the CSSC manager object
    var csscManager = createCSSCManager();
    // Replace the existing array with our implementation
    window.wvusCSSC = csscManager;
    // Gather CSSC from various sources
    try {
        gatherCSSCSources(csscManager);
    }
    catch (err) {
        // ignore errors during gathering sources
    }
    // Process any existing queue items after gatherCSSCSources so they don't
    // set the cookie and thus assume the importance of the cookie assertion origin.
    if (existingQueue.length > 0) {
        existingQueue.forEach(function (item) {
            if (item && typeof item === 'object' && 'origin' in item && 'value' in item) {
                csscManager.push(item);
            }
        });
    }
};
// Self-executing function to initialize the module
(function () {
    initializeCSCC();
})();
